import { useEffect, useState, useRef } from 'react';
import { useSelector } from "react-redux";
import { tokens } from "../../theme";
import { useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import 'dayjs/locale/en-gb';
import dayjs from 'dayjs';
import { __fetching_data__ } from '../../components/api.js';
import './bidask.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

function createData(vol_bid, bid, ask, vol_ask) {
    return { vol_bid, bid, ask, vol_ask };
}

function createDataTickmatch(open, high, low, close) {
    return { open, high, low, close };
}

const formatTime = (time) => {
    if (typeof time === 'object') {
        return `${dayjs(time).hour()}:${String(dayjs(time).minute()).padStart(2, '0')}`;
    }
    return time;
};

const formatDate = (date) => {
    if (typeof date === 'object') {
        return `${dayjs(date).year()}-${String(dayjs(date).month() + 1).padStart(2, '0')}-${String(dayjs(date).date()).padStart(2, '0')}`;
    }
    return date;
};

const objectToQueryString = (obj) =>
    Object.keys(obj).map(k => encodeURIComponent(k) + '=' + encodeURIComponent(obj[k])).join('&');

const NumberWithCommasAndDecimal = (number, decimalPlaces) => {
    const formatted = decimalPlaces === 2
        ? Number(number).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : Number(number).toLocaleString('en-US');
    return <div>{formatted}</div>;
};

const hightLow = (values, key) => ({
    max: Math.max(...values.map(item => parseFloat(item[key]))),
    min: Math.min(...values.map(item => parseFloat(item[key]))),
});

function createInitialRows(depth = 10) {
    const safeDepth = Math.max(10, Math.min(depth, 50));
    const rows = [];
    for (let i = 0; i < safeDepth; i++) rows.push(createData(0, 0, 0, 0));
    rows.push(createData("", "", "", ""));
    rows.push(createData("Sum Bid", "", "", "Sum Ask"));
    rows.push(createData(0, "", "", 0));
    return rows;
}

const initialInRange = [createDataTickmatch(0, 0, 0, 0)];
const initialActual  = [createDataTickmatch(0, 0, 0, 0)];

// ─── Skeleton Components ─────────────────────────────────────────────────────

const skeletonCSS = `
@keyframes baShimmer {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}
`;

function Shimmer({ delay = 0, speed = 1.8 }) {
    return (
        <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.07) 40%, rgba(125,211,252,0.15) 50%, rgba(56,189,248,0.07) 60%, transparent 100%)',
            animation: `baShimmer ${speed}s ease-in-out ${delay}s infinite`,
        }} />
    );
}

function SkeletonBlock({ w = '100%', h = 10, radius = 4, delay = 0, style = {} }) {
    return (
        <div style={{
            width: w, height: h, borderRadius: radius,
            backgroundColor: '#1e293b',
            position: 'relative', overflow: 'hidden', flexShrink: 0,
            ...style,
        }}>
            <Shimmer delay={delay} />
        </div>
    );
}

/** skeleton row for the order book — 4 columns */
function SkeletonOrderRow({ index }) {
    const bidVolW = 36 + (index % 4) * 8;
    const bidW    = 32 + (index % 3) * 6;
    const askW    = 32 + ((index + 1) % 3) * 6;
    const askVolW = 36 + ((index + 2) % 4) * 8;
    const delay   = index * 0.04;
    return (
        <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
            height: 32, alignItems: 'center',
            borderBottom: '1px solid #0f172a',
            backgroundColor: index % 2 === 0 ? 'rgba(15,23,42,0.4)' : 'transparent',
            padding: '0 8px',
        }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <SkeletonBlock w={bidVolW} h={9} delay={delay} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <SkeletonBlock w={bidW} h={9} delay={delay + 0.05} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <SkeletonBlock w={askW} h={9} delay={delay + 0.05} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <SkeletonBlock w={askVolW} h={9} delay={delay} />
            </div>
        </div>
    );
}

/** skeleton for StatSection (In Range / Actual) */
function SkeletonStatSection({ delay = 0 }) {
    return (
        <div style={{ backgroundColor: '#111827', border: '1px solid #334155', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ backgroundColor: '#1e293b', padding: '8px 12px' }}>
                <SkeletonBlock w={60} h={9} delay={delay} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '8px 12px 4px' }}>
                {['OPEN','HIGH','LOW','CLOSE'].map((_, i) => (
                    <SkeletonBlock key={i} w={28} h={7} delay={delay + i * 0.05} />
                ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '4px 12px 12px' }}>
                {[40, 48, 48, 40].map((w, i) => (
                    <SkeletonBlock key={i} w={w} h={12} delay={delay + 0.15 + i * 0.05} />
                ))}
            </div>
        </div>
    );
}

function OrderBookSkeleton({ rowCount = 10 }) {
    return (
        <div style={{
            position: 'absolute', inset: 0, zIndex: 40,
            backgroundColor: 'rgba(11,17,26,0.96)',
            display: 'flex', flexDirection: 'column',
            borderRadius: '0 0 12px 12px',
            overflow: 'hidden',
        }}>
            <style>{skeletonCSS}</style>

            <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
                backgroundColor: '#0f172a', borderBottom: '1px solid #1e293b',
                padding: '8px', flexShrink: 0,
            }}>
                {['Vol BID', 'BID', 'ASK', 'Vol ASK'].map((label, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: i === 0 ? 'flex-end' : i === 3 ? 'flex-start' : 'center', padding: '0 8px' }}>
                        <SkeletonBlock w={36} h={8} delay={i * 0.06} />
                    </div>
                ))}
            </div>

            <div style={{ flex: 1, overflowY: 'hidden' }}>
                {[...Array(rowCount)].map((_, i) => (
                    <SkeletonOrderRow key={i} index={i} />
                ))}
                <div style={{ height: 28, backgroundColor: '#111827', borderBottom: '1px solid #1e293b' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', height: 28, alignItems: 'center', backgroundColor: '#111827', borderBottom: '1px solid #1e293b', padding: '0 8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <SkeletonBlock w={52} h={9} delay={0.2} />
                    </div>
                    <div /><div />
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <SkeletonBlock w={52} h={9} delay={0.2} />
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', height: 36, alignItems: 'center', backgroundColor: '#111827', borderTop: '1px solid #334155', padding: '0 8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <SkeletonBlock w={72} h={10} delay={0.25} />
                    </div>
                    <div /><div />
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <SkeletonBlock w={72} h={10} delay={0.25} />
                    </div>
                </div>
            </div>

            <div style={{ padding: '12px 16px', backgroundColor: '#0f172a', borderTop: '1px solid #1e293b', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, height: 3, backgroundColor: '#1e293b', borderRadius: 9999, position: 'relative', overflow: 'hidden' }}>
                        <Shimmer delay={0.3} />
                    </div>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#1e293b', position: 'relative', overflow: 'hidden' }}>
                        <Shimmer delay={0.3} />
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: 16, backgroundColor: '#0f172a', borderTop: '1px solid #1e293b', flexShrink: 0 }}>
                <SkeletonStatSection delay={0} />
                <SkeletonStatSection delay={0.1} />
            </div>
        </div>
    );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatSection({ title, data }) {
    const open  = data?.open  ?? '–';
    const high  = data?.high  ?? '–';
    const low   = data?.low   ?? '–';
    const close = data?.close ?? '–';

    const extract = (v) => {
        if (!v || v === 0) return '–';
        if (typeof v === 'object' && v?.props?.children) return v.props.children;
        return v;
    };

    return (
        <div className="bg-[#111827] border border-slate-700 rounded">
            <div className="px-3 py-2 text-[11px] text-slate-400 border-b border-slate-700 bg-[#1e293b]">{title}</div>
            <div className="grid grid-cols-4 text-[10px] text-slate-500 px-3 pt-2">
                <span>OPEN</span><span>HIGH</span><span>LOW</span><span>CLOSE</span>
            </div>
            <div className="grid grid-cols-4 px-3 pb-3 pt-1 text-[13px] font-semibold text-white">
                <span>{extract(open)}</span>
                <span>{typeof high === 'number' ? high.toFixed(2) : extract(high)}</span>
                <span>{typeof low  === 'number' ? low.toFixed(2)  : extract(low)}</span>
                <span>{extract(close)}</span>
            </div>
        </div>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────

const BaseBidAskComponent = (page, country) => {
    const class_page_name = '.change-tracking' + page + ' td';
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const csrfToken = useSelector((state) => state.csrfToken);

    const now = dayjs();
    const today = now.format('YYYY-MM-DD');
    const today_value = dayjs(today).subtract(0, 'day');

    const [stHour, stMinute] = [10, 0];
    const [enHour, enMinute] = [16, 30];
    const stEnTimeMin = dayjs().set('hour', stHour).set('minute', stMinute);
    const stEnTimeMax = dayjs().set('hour', enHour).set('minute', enMinute);

    const [depth, setDepth] = useState(10);
    const initialRow = createInitialRows(depth);

    const [loading, setLoading]               = useState(false);
    const [rows, setRows]                     = useState(initialRow);
    const [rowsInRange, setRowsInRange]       = useState(initialInRange);
    const [rowsActual, setRowsActual]         = useState(initialActual);

    const [initialValues, setInitialValues] = useState({
        start_date: today_value,
        end_date:   today_value,
        start_time: stEnTimeMin,
        end_time:   stEnTimeMax,
        symbol: "",
        speed: 1,
    });

    const [countRecordBidAsk, setCountRecordBidAsk] = useState(0);
    const [bidaskLength, setBidAskLength]           = useState(1);
    const [resultBidAsk, setResultBidAsk]           = useState([]);
    const [resultTickMatch, setResultTickMatch]     = useState([]);

    const [offset, setOffset]   = useState(0);
    const [limit]               = useState(500);
    const [hasMore, setHasMore] = useState(true);

    const [symbolHistory, setSymbolHistory]     = useState([]);
    const [filteredSymbols, setFilteredSymbols] = useState([]);
    const [showDropdown, setShowDropdown]       = useState(false);
    const [isFocused, setIsFocused]             = useState(false);

    useEffect(() => {
        const sym = initialValues.symbol;
        if (!sym.trim()) { setFilteredSymbols(symbolHistory); return; }
        setFilteredSymbols(symbolHistory.filter(s => s.toLowerCase().includes(sym.toLowerCase())));
    }, [initialValues.symbol, symbolHistory]);

    // ─── Data transform ──────────────────────────────────────────────────────

    function transformOrderBookArray(orderBooks, symbolOverride = "AAV") {
        const formatPrice  = (p) => Number(p).toFixed(2);
        const formatVolume = (v) => Math.round(Number(v)).toString();
        const safeDepth = Math.max(10, Math.min(depth, 50));
        return orderBooks.map((data) => {
            const result = {};
            const asks = Array.isArray(data.asks) ? data.asks.map(([price, volume]) => ({ price, volume })) : [];
            const bids = Array.isArray(data.bids) ? data.bids.map(([price, volume]) => ({ price, volume })) : [];
            while (asks.length < safeDepth) asks.push({ price: 0.0, volume: 0 });
            while (bids.length < safeDepth) bids.push({ price: 0.0, volume: 0 });
            asks.sort((a, b) => a.price - b.price).slice(0, safeDepth).forEach((ask, i) => {
                result[`Ask${i + 1}`] = formatPrice(ask.price);
                result[`VolAsk${i + 1}`] = formatVolume(ask.volume);
            });
            bids.sort((a, b) => b.price - a.price).slice(0, safeDepth).forEach((bid, i) => {
                result[`Bid${i + 1}`] = formatPrice(bid.price);
                result[`VolBid${i + 1}`] = formatVolume(bid.volume);
            });
            try {
                const iso = data.timestamp_th;
                const [datePart, timePart] = iso.split("T");
                result["Time"] = `${datePart} ${timePart.split("+")[0].slice(0, 8)}`;
            } catch { result["Time"] = "Invalid Date"; }
            result["Symbol"] = data.symbol || symbolOverride;
            return result;
        });
    }

    // ─── Effects ─────────────────────────────────────────────────────────────

    useEffect(() => { setDepth(country === 'usa' ? 50 : 10); }, []);
    useEffect(() => { setRows(createInitialRows(depth)); }, [depth]);

    useEffect(() => {
        if (dayjs(initialValues.end_date).isSame(dayjs(), 'day')) {
            const interval = setInterval(() => {
                const n = dayjs();
                const maxTime = dayjs().set('hour', 16).set('minute', 30);
                setInitialValues(prev => ({ ...prev, end_time: n.isAfter(maxTime) ? maxTime.format("HH:mm") : n.format("HH:mm") }));
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [initialValues.end_date]);

    // ─── Reset / Fetch ────────────────────────────────────────────────────────

    const resetProcess = () => {
        previousBidAsk.current = {};
        previousTickMatchInRange.current = {};
        previousTickMatchActual.current = {};
        document.querySelectorAll(class_page_name).forEach(cell => { cell.style.backgroundColor = ''; });
        setRows(initialRow);
        setRowsInRange(initialInRange);
        setRowsActual(initialActual);
        setCountRecordBidAsk(0);
        setIsPlaying(false);
        setTime2Show('00:00:00');
    };

    const fetchData = async (currentOffset) => {
        const values = {
            ...initialValues,
            start_date: formatDate(initialValues.start_date),
            end_date:   formatDate(initialValues.end_date),
            start_time: formatTime(initialValues.start_time),
            end_time:   formatTime(initialValues.end_time),
            country, limit, offset: currentOffset,
        };
        const response = await __fetching_data__({
            url: `${window.config.base_api_url}/bidask_dejavu_report?${objectToQueryString(values)}`,
            method: "GET",
            headers: { "xsrf-token": csrfToken },
        });
        if (response.status) {
            let transformed = response.data.bidask;
            if (country === "usa") transformed = transformOrderBookArray(transformed, initialValues.symbol);
            setResultBidAsk(prev => [...prev, ...transformed]);
            if (country !== "usa") setResultTickMatch(prev => [...prev, ...response.data.tickmatch]);
            const totalRows = country === "usa" ? response.total.bidask : response.data.bidask.length;
            setBidAskLength(totalRows);
            if (country === "usa") {
                const nextOffset = currentOffset + limit;
                setOffset(nextOffset);
                if (nextOffset >= totalRows) { setHasMore(false); }
                else fetchData(nextOffset);
            }
        }
    };

    const handleSubmitForm = async () => {
        if (!initialValues.symbol.trim()) return;
        const updated = [initialValues.symbol, ...symbolHistory.filter(s => s !== initialValues.symbol)].slice(0, 10);
        setSymbolHistory(updated);
        setLoading(true);
        resetProcess();
        setOffset(0);
        setHasMore(true);
        setResultBidAsk([]);
        await fetchData(0);
        setLoading(false);
        setIsPlaying(true);
    };

    // ─── Form Handlers ────────────────────────────────────────────────────────

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInitialValues(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (name, value) => {
        setInitialValues(prev => {
            let updated = { ...prev, [name]: value };
            if (name === 'start_date') {
                // If new start_date is after current end_date, push end_date forward to match
                if (dayjs(value).isAfter(dayjs(prev.end_date))) {
                    updated.end_date = value;
                }
            }
            // If end_date is today, let the interval keep end_time live
            // If end_date is moved to a past date, set end_time to market close
            if (name === 'end_date') {
                if (dayjs(value).isBefore(dayjs(), 'day')) {
                    updated.end_time = stEnTimeMax.format("HH:mm");
                }
            }
            return updated;
        });
    };

    const handleTimeChange = (name, value) => {
        setInitialValues(prev => ({
            ...prev,
            [name]: `${String(dayjs(value).hour()).padStart(2, '0')}:${String(dayjs(value).minute()).padStart(2, '0')}`
        }));
    };

    // ─── Playback ─────────────────────────────────────────────────────────────

    const [isPlaying, setIsPlaying]         = useState(false);
    const [Time2Show, setTime2Show]         = useState('00:00:00');
    const [speedChanged, setSpeedChanged]   = useState(false);
    const currentIndexRef                   = useRef(0);
    const previousBidAsk                    = useRef({});
    const previousTickMatchInRange          = useRef({});
    const previousTickMatchActual           = useRef({});
    const prevSpeedRef                      = useRef(initialValues.speed);

    const handleClickPlayPause = () => setIsPlaying(p => !p);

    const handleSliderChange = (e) => {
        const newValue = Number(e.target.value);
        currentIndexRef.current = countRecordBidAsk;
        setCountRecordBidAsk(newValue);
        if (resultBidAsk[newValue - 1]) {
            setTime2Show(dayjs(resultBidAsk[newValue - 1].Time).format('HH:mm:ss'));
        }
    };

    const extractAndConvertChildren = (obj) =>
        Object.keys(obj).reduce((acc, key) => {
            if (obj[key]) {
                if (typeof obj[key] === 'object' && obj[key]?.props?.children) {
                    acc[key] = parseFloat(obj[key].props.children.replace(/,/g, ''));
                } else { acc[key] = obj[key]; }
            }
            return acc;
        }, {});

    const compareData = (oldData, newData) => {
        const differences = [];
        oldData.forEach((oldRow, index) => {
            const newRow = newData[index];
            if (!newRow) return;
            const diff = {};
            for (const key in oldRow) {
                if (oldRow[key] !== newRow[key]) {
                    const oldVal = parseFloat(oldRow[key]) || 0;
                    const newVal = parseFloat(newRow[key]) || 0;
                    const change = newVal - oldVal;
                    diff[key] = { old: oldVal, new: newVal, change, type: change === 0 ? "" : change > 0 ? 'positive' : 'negative', rowIndex: index };
                }
            }
            if (Object.keys(diff).length > 0) differences.push({ index, diff });
        });
        return differences;
    };

    const isEmpty = (obj) => { for (let k in obj) if (obj.hasOwnProperty(k)) return false; return true; };

    useEffect(() => {
        let interval;
        let speed = initialValues.speed == 0 || initialValues.speed == null ? 1 : initialValues.speed;
        if (isPlaying) {
            interval = setInterval(async () => {
                speed = initialValues.speed == 0 || initialValues.speed == null ? 1 : initialValues.speed;
                if (speedChanged) { setLoading(true); await delay(2000); setLoading(false); }
                let time_minute_search = 0;
                setCountRecordBidAsk(prevCount => {
                    if (prevCount >= bidaskLength - 1) { setIsPlaying(false); clearInterval(interval); return prevCount; }
                    let bidask_per_sec = resultBidAsk[prevCount];
                    if (!bidask_per_sec) { alert("รอข้อมูลก่อน..."); return currentIndexRef.current; }

                    time_minute_search = dayjs(bidask_per_sec['Time']).format('YYYY-MM-DD HH:mm');
                    setTime2Show(dayjs(bidask_per_sec['Time']).format('HH:mm:ss'));

                    const isBefore = dayjs(formatDate(initialValues.start_date)).isBefore(dayjs('2023-05-08'));
                    document.querySelectorAll(class_page_name).forEach(cell => { cell.style.backgroundColor = ''; });

                    let currentBidAsk = [];
                    const updatedRows = [...rows];

                    if (isBefore) {
                        if (bidask_per_sec) {
                            currentBidAsk[0] = createData(NumberWithCommasAndDecimal(bidask_per_sec.VolBid1), NumberWithCommasAndDecimal(bidask_per_sec.Bid1, 2), NumberWithCommasAndDecimal(bidask_per_sec.Ask5, 2), NumberWithCommasAndDecimal(bidask_per_sec.VolAsk5));
                            currentBidAsk[1] = createData(NumberWithCommasAndDecimal(bidask_per_sec.VolBid2), NumberWithCommasAndDecimal(bidask_per_sec.Bid2, 2), NumberWithCommasAndDecimal(bidask_per_sec.Ask4, 2), NumberWithCommasAndDecimal(bidask_per_sec.VolAsk4));
                            currentBidAsk[2] = createData(NumberWithCommasAndDecimal(bidask_per_sec.VolBid3), NumberWithCommasAndDecimal(bidask_per_sec.Bid3, 2), NumberWithCommasAndDecimal(bidask_per_sec.Ask3, 2), NumberWithCommasAndDecimal(bidask_per_sec.VolAsk3));
                            currentBidAsk[3] = createData(NumberWithCommasAndDecimal(bidask_per_sec.VolBid4), NumberWithCommasAndDecimal(bidask_per_sec.Bid4, 2), NumberWithCommasAndDecimal(bidask_per_sec.Ask2, 2), NumberWithCommasAndDecimal(bidask_per_sec.VolAsk2));
                            currentBidAsk[4] = createData(NumberWithCommasAndDecimal(bidask_per_sec.VolBid5), NumberWithCommasAndDecimal(bidask_per_sec.Bid5, 2), NumberWithCommasAndDecimal(bidask_per_sec.Ask1, 2), NumberWithCommasAndDecimal(bidask_per_sec.VolAsk1));
                            currentBidAsk[12] = createData(
                                NumberWithCommasAndDecimal(parseFloat(bidask_per_sec.VolBid1) + parseFloat(bidask_per_sec.VolBid2) + parseFloat(bidask_per_sec.VolBid3) + parseFloat(bidask_per_sec.VolBid4) + parseFloat(bidask_per_sec.VolBid5)),
                                "", "",
                                NumberWithCommasAndDecimal(parseFloat(bidask_per_sec.VolAsk1) + parseFloat(bidask_per_sec.VolAsk2) + parseFloat(bidask_per_sec.VolAsk3) + parseFloat(bidask_per_sec.VolAsk4) + parseFloat(bidask_per_sec.VolAsk5)),
                            );
                            const extractedRows = currentBidAsk.map(extractAndConvertChildren);
                            if (!isEmpty(previousBidAsk.current)) {
                                compareData(previousBidAsk.current, extractedRows).forEach(obj => {
                                    Object.entries(obj.diff).forEach(([key, value]) => {
                                        const cell = document.querySelector(`#cell-${value.rowIndex}-${key}`);
                                        if (cell) {
                                            cell.style.backgroundColor = value.type === 'positive' ? 'lightgreen' : 'lightcoral';
                                            cell.style.color = 'black';
                                            setTimeout(() => { cell.style.color = 'white'; }, 1000);
                                        }
                                    });
                                });
                            }
                            [0,1,2,3,4].forEach(i => { updatedRows[i] = currentBidAsk[i]; });
                            updatedRows[12] = currentBidAsk[12];
                            previousBidAsk.current = extractedRows;
                        }
                    } else {
                        for (let i = 1; i <= depth; i++) {
                            currentBidAsk[i - 1] = createData(
                                NumberWithCommasAndDecimal(bidask_per_sec[`VolBid${i}`]),
                                NumberWithCommasAndDecimal(bidask_per_sec[`Bid${i}`], 2),
                                NumberWithCommasAndDecimal(bidask_per_sec[`Ask${depth - i + 1}`], 2),
                                NumberWithCommasAndDecimal(bidask_per_sec[`VolAsk${depth - i + 1}`])
                            );
                        }
                        let sumBid = 0, sumAsk = 0;
                        for (let i = 1; i <= depth; i++) {
                            sumBid += parseFloat(bidask_per_sec[`VolBid${i}`] || 0);
                            sumAsk += parseFloat(bidask_per_sec[`VolAsk${i}`] || 0);
                        }
                        currentBidAsk[depth + 2] = createData(NumberWithCommasAndDecimal(sumBid), "", "", NumberWithCommasAndDecimal(sumAsk));
                        const extractedRows = currentBidAsk.map(extractAndConvertChildren);
                        if (!isEmpty(previousBidAsk.current)) {
                            compareData(previousBidAsk.current, extractedRows).forEach(obj => {
                                Object.entries(obj.diff).forEach(([key, value]) => {
                                    const cell = document.querySelector(`#cell-${page}-${value.rowIndex}-${key}`);
                                    if (cell) {
                                        cell.style.backgroundColor = value.type === "positive" ? "lightgreen" : "lightcoral";
                                        cell.style.color = "black";
                                        setTimeout(() => { cell.style.color = "white"; }, 1000);
                                    }
                                });
                            });
                        }
                        for (let i = 0; i < depth; i++) updatedRows[i] = currentBidAsk[i];
                        updatedRows[depth + 2] = currentBidAsk[depth + 2];
                        previousBidAsk.current = extractedRows;
                    }

                    setRows(updatedRows);

                    if (resultTickMatch) {
                        let tickmatch_hight_low;
                        const tickmatch_per_min = resultTickMatch.filter(x => x.DtMinOfRec.substring(0, x.DtMinOfRec.length - 3) === time_minute_search);
                        const tickmatch_in_range = resultTickMatch.filter(x => {
                            const x1  = dayjs(x.DtMinOfRec);
                            const x21 = dayjs(formatDate(initialValues.start_date) + " " + formatTime(initialValues.start_time));
                            const x22 = dayjs(formatDate(initialValues.end_date)   + " " + formatTime(initialValues.end_time));
                            return x1 >= x21 && x1 <= x22;
                        });
                        if (tickmatch_per_min.length > 0) tickmatch_hight_low = hightLow(tickmatch_per_min, 'Last');
                        const updatedRowsInRange = [...rowsInRange];
                        const InRangeTickmatch = createDataTickmatch(
                            tickmatch_in_range.length > 0 ? NumberWithCommasAndDecimal(tickmatch_in_range[0].Last, 2) : 0,
                            tickmatch_hight_low?.max ?? previousTickMatchInRange?.current?.high ?? 0,
                            tickmatch_hight_low?.min ?? previousTickMatchInRange?.current?.low  ?? 0,
                            tickmatch_in_range.length > 0 ? NumberWithCommasAndDecimal(tickmatch_in_range[tickmatch_in_range.length - 1].Last, 2) : 0
                        );
                        updatedRowsInRange[0] = InRangeTickmatch;
                        previousTickMatchInRange.current = {
                            open:  InRangeTickmatch.open?.props  ? parseFloat(InRangeTickmatch.open.props.children) : 0,
                            high:  parseFloat(InRangeTickmatch.high),
                            low:   parseFloat(InRangeTickmatch.low),
                            close: InRangeTickmatch.close?.props ? parseFloat(InRangeTickmatch.close.props.children) : 0,
                        };
                        setRowsInRange(updatedRowsInRange);

                        const updatedRowsActual = [...rowsActual];
                        const allHL = hightLow(resultTickMatch, 'Last');
                        const ActualTickmatch = createDataTickmatch(
                            resultTickMatch.length > 0 ? NumberWithCommasAndDecimal(resultTickMatch[0].Last, 2) : 0,
                            allHL?.max ?? previousTickMatchActual?.current?.high ?? 0,
                            allHL?.min ?? previousTickMatchActual?.current?.low  ?? 0,
                            resultTickMatch.length > 0 ? NumberWithCommasAndDecimal(resultTickMatch[resultTickMatch.length - 1].Last, 2) : 0
                        );
                        updatedRowsActual[0] = ActualTickmatch;
                        previousTickMatchActual.current = {
                            open:  ActualTickmatch.open?.props  ? parseFloat(ActualTickmatch.open.props.children) : 0,
                            high:  ActualTickmatch.high  ? parseFloat(ActualTickmatch.high)  : 0,
                            low:   ActualTickmatch.low   ? parseFloat(ActualTickmatch.low)   : 0,
                            close: ActualTickmatch.close?.props ? parseFloat(ActualTickmatch.close.props.children) : 0,
                        };
                        setRowsActual(updatedRowsActual);
                    }

                    if (speed !== prevSpeedRef.current) setSpeedChanged(true);
                    else setSpeedChanged(false);
                    prevSpeedRef.current = speed;
                    return prevCount + 1;
                });
            }, 1000 / speed);
        }
        return () => clearInterval(interval);
    }, [isPlaying, countRecordBidAsk, initialValues.speed]);

    // ─── Derived totals ──────────────────────────────────────────────────────

    const totalBid = rows.slice(0, depth).reduce((sum, row) => {
        const v = row?.vol_bid;
        if (!v || v === 0) return sum;
        const num = typeof v === 'object' ? parseFloat(v?.props?.children?.replace?.(/,/g, '') || 0) : parseFloat(v) || 0;
        return sum + num;
    }, 0);

    const totalAsk = rows.slice(0, depth).reduce((sum, row) => {
        const v = row?.vol_ask;
        if (!v || v === 0) return sum;
        const num = typeof v === 'object' ? parseFloat(v?.props?.children?.replace?.(/,/g, '') || 0) : parseFloat(v) || 0;
        return sum + num;
    }, 0);

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="bg-[#111827] border border-slate-700 rounded-xl overflow-hidden flex flex-col h-full min-h-0">

            {/* HEADER — always interactive, never covered by skeleton */}
            <div className="p-4 border-b border-slate-700 bg-[#0f172a] shrink-0">

                {/* Row 1: Symbol / Start Date / End Date */}
                <div className="grid grid-cols-3 gap-3 mb-3">

                    {/* Symbol */}
                    <div className="relative">
                        <div className={`bg-[#111827] border rounded-md px-3 py-2 flex items-center transition-colors ${isFocused ? 'border-cyan-500' : 'border-slate-600'}`}>
                            <input
                                name="symbol"
                                value={initialValues.symbol}
                                onChange={(e) => { handleChange(e); setShowDropdown(true); }}
                                onFocus={() => { setIsFocused(true); setShowDropdown(true); }}
                                onBlur={() => { setTimeout(() => setShowDropdown(false), 150); setIsFocused(false); }}
                                className="w-full bg-transparent outline-none text-white text-xs"
                            />
                        </div>
                        {showDropdown && filteredSymbols.length > 0 && (
                            <div className="absolute left-0 right-0 mt-1 bg-[#0f172a] border border-slate-700 rounded-md shadow-lg z-50 max-h-40 overflow-y-auto">
                                {filteredSymbols.map((item, idx) => (
                                    <div key={idx} onMouseDown={() => { setInitialValues(prev => ({ ...prev, symbol: item })); setShowDropdown(false); }}
                                        className="px-3 py-2 text-xs text-white hover:bg-indigo-600 cursor-pointer transition">
                                        {item}
                                    </div>
                                ))}
                            </div>
                        )}
                        <label className={`absolute left-3 text-[10px] px-1 bg-[#0f172a] text-slate-300 pointer-events-none transition-all duration-200 ${isFocused || initialValues.symbol ? '-top-2' : 'top-2'}`}>
                            Symbol*
                        </label>
                    </div>

                    {/* Start Date */}
                    <div className="relative">
                        <div className="bg-[#111827] border border-slate-600 rounded-md px-3 py-2">
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                                <DatePicker
                                    value={initialValues.start_date}
                                    maxDate={today_value}
                                    onChange={(v) => handleDateChange('start_date', v)}
                                    slotProps={{ textField: { size: 'small', variant: 'standard', InputProps: { disableUnderline: true, sx: { color: 'white', fontSize: '12px' } } } }}
                                />
                            </LocalizationProvider>
                        </div>
                        <label className="absolute left-3 -top-2 text-[10px] px-1 bg-[#0f172a] text-slate-300">Start Date</label>
                    </div>

                    {/* End Date — now fully interactive */}
                    <div className="relative">
                        <div className="bg-[#111827] border border-slate-600 rounded-md px-3 py-2">
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                                <DatePicker
                                    value={initialValues.end_date}
                                    minDate={initialValues.start_date}
                                    maxDate={today_value}
                                    onChange={(v) => handleDateChange('end_date', v)}
                                    slotProps={{ textField: { size: 'small', variant: 'standard', InputProps: { disableUnderline: true, sx: { color: 'white', fontSize: '12px' } } } }}
                                />
                            </LocalizationProvider>
                        </div>
                        <label className="absolute left-3 -top-2 text-[10px] px-1 bg-[#0f172a] text-slate-300">End Date</label>
                    </div>
                </div>

                {/* Row 2: Start Time / End Time / Speed + Search */}
                <div className="grid grid-cols-3 gap-3">

                    {/* Start Time */}
                    <div className="relative">
                        <div className="bg-[#111827] border border-slate-600 rounded-md px-3 py-2">
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                                <TimePicker
                                    defaultValue={initialValues.start_time}
                                    ampm={false}
                                    minTime={stEnTimeMin.subtract(1, 'minute')}
                                    maxTime={stEnTimeMax.add(1, 'minute')}
                                    onChange={(v) => handleTimeChange('start_time', v)}
                                    slotProps={{ textField: { size: 'small', variant: 'standard', InputProps: { disableUnderline: true, sx: { color: 'white', fontSize: '12px' } } } }}
                                />
                            </LocalizationProvider>
                        </div>
                        <label className="absolute left-3 -top-2 text-[10px] px-1 bg-[#0f172a] text-slate-300">Start Time</label>
                    </div>

                    {/* End Time (locked) */}
                    <div className="relative opacity-60">
                        <div className="bg-[#0f172a] border border-slate-700 rounded-md px-3 py-2">
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                                <TimePicker
                                    value={dayjs(initialValues.end_time, "HH:mm")}
                                    disabled ampm={false}
                                    slotProps={{ textField: { size: 'small', variant: 'standard', InputProps: { disableUnderline: true, sx: { color: '#64748b', fontSize: '12px' } } } }}
                                />
                            </LocalizationProvider>
                        </div>
                        <label className="absolute left-3 -top-2 text-[10px] px-1 bg-[#0f172a] text-slate-300">End Time</label>
                    </div>

                    {/* Speed + Search */}
                    <div className="grid grid-cols-2 gap-2">
                        <div className="relative">
                            <div className="bg-[#111827] border border-slate-600 rounded-md px-3 py-2">
                                <input
                                    type="number" name="speed" value={initialValues.speed}
                                    min="1" max="100" step="1"
                                    onChange={handleChange}
                                    className="w-full bg-transparent outline-none text-white text-xs"
                                />
                            </div>
                            <label className="absolute left-3 -top-2 text-[10px] px-1 bg-[#0f172a] text-slate-300">Speed</label>
                        </div>
                        <button
                            onClick={handleSubmitForm}
                            className="w-full bg-indigo-600 hover:bg-indigo-500 rounded-md text-sm font-semibold text-white transition active:scale-95"
                        >
                            SEARCH
                        </button>
                    </div>
                </div>

                {/* Loading indicator */}
                {loading && (
                    <div style={{ marginTop: 10, height: 2, borderRadius: 9999, backgroundColor: '#1e293b', overflow: 'hidden', position: 'relative' }}>
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(90deg, transparent, #38bdf8, #7dd3fc, #38bdf8, transparent)',
                            animation: 'baShimmer 1.2s ease-in-out infinite',
                        }} />
                        <style>{skeletonCSS}</style>
                    </div>
                )}

                {/* Time display */}
                <div className="mt-3 bg-black text-yellow-400 font-mono text-center py-2 rounded text-sm tracking-widest">
                    {Time2Show}
                </div>
            </div>

            {/* BODY — order book + slider + stats, with skeleton overlay when loading */}
            <div className="relative flex flex-col flex-1 min-h-0">

                {loading && <OrderBookSkeleton rowCount={depth} />}

                {/* ORDER BOOK */}
                <div className={`flex-1 overflow-y-auto min-h-0 bg-[#0b111a] change-tracking${page}`}>

                    {/* Header row */}
                    <div className="grid grid-cols-4 text-[11px] text-slate-500 bg-[#0f172a] border-b border-slate-700 sticky top-0 z-10">
                        <div className="text-right pr-3 py-2">Vol BID</div>
                        <div className="text-center py-2">BID</div>
                        <div className="text-center py-2">ASK</div>
                        <div className="text-left pl-3 py-2">Vol ASK</div>
                    </div>

                    {rows.map((row, index) => {
                        const isSpecialRow = index >= depth;
                        if (isSpecialRow) {
                            return (
                                <div key={index} className="grid grid-cols-4 items-center text-[11px] h-[28px] border-b border-slate-800 bg-[#111827] font-semibold text-slate-400">
                                    <div id={`cell-${page}-${index}-vol_bid`} className="text-right pr-3">{row.vol_bid}</div>
                                    <div id={`cell-${page}-${index}-bid`}     className="text-center">{row.bid}</div>
                                    <div id={`cell-${page}-${index}-ask`}     className="text-center">{row.ask}</div>
                                    <div id={`cell-${page}-${index}-vol_ask`} className="text-left pl-3">{row.vol_ask}</div>
                                </div>
                            );
                        }

                        const bidVolRaw = typeof row.vol_bid === 'object' ? row.vol_bid?.props?.children ?? '0' : String(row.vol_bid);
                        const askVolRaw = typeof row.vol_ask === 'object' ? row.vol_ask?.props?.children ?? '0' : String(row.vol_ask);
                        const bidText   = typeof row.bid === 'object' ? row.bid?.props?.children ?? '-' : String(row.bid);
                        const askText   = typeof row.ask === 'object' ? row.ask?.props?.children ?? '-' : String(row.ask);

                        return (
                            <div key={index}
                                className="grid grid-cols-4 items-center text-[12px] h-[32px] border-b border-slate-800 relative"
                                style={{ backgroundColor: index % 2 === 0 ? 'rgba(15,23,42,0.4)' : 'transparent' }}
                            >
                                <div id={`cell-${page}-${index}-vol_bid`} className="relative text-right pr-3 text-slate-300 font-medium overflow-hidden">
                                    <div className="absolute right-0 top-0 h-full bg-blue-900/40"
                                        style={{ width: `${Math.min((parseInt(bidVolRaw.replace(/,/g, '')) || 0) / 500000 * 100, 100)}%` }} />
                                    <span className="relative z-10">{row.vol_bid}</span>
                                </div>
                                <div id={`cell-${page}-${index}-bid`} className="text-center text-green-400 font-semibold">{bidText}</div>
                                <div id={`cell-${page}-${index}-ask`} className="text-center text-red-400 font-semibold">{askText}</div>
                                <div id={`cell-${page}-${index}-vol_ask`} className="relative text-left pl-3 text-slate-300 font-medium overflow-hidden">
                                    <div className="absolute left-0 top-0 h-full bg-red-900/40"
                                        style={{ width: `${Math.min((parseInt(askVolRaw.replace(/,/g, '')) || 0) / 500000 * 100, 100)}%` }} />
                                    <span className="relative z-10">{row.vol_ask}</span>
                                </div>
                            </div>
                        );
                    })}

                    {/* Total sticky row */}
                    <div className="grid grid-cols-4 h-[36px] items-center border-t border-slate-700 bg-[#111827] text-[12px] font-semibold sticky bottom-0">
                        <div className="text-right pr-3 text-blue-400">Total: {totalBid.toLocaleString()}</div>
                        <div /><div />
                        <div className="text-left pl-3 text-red-400">Total: {totalAsk.toLocaleString()}</div>
                    </div>
                </div>

                {/* SLIDER */}
                <div className="px-4 py-3 bg-[#0f172a] border-t border-slate-700 shrink-0">
                    <div className="flex items-center gap-4">
                        <input
                            type="range" min={1} max={bidaskLength} value={countRecordBidAsk}
                            onChange={handleSliderChange}
                            className="flex-1 h-[3px] appearance-none bg-slate-600 rounded-full
                                [&::-webkit-slider-thumb]:appearance-none
                                [&::-webkit-slider-thumb]:w-4
                                [&::-webkit-slider-thumb]:h-4
                                [&::-webkit-slider-thumb]:bg-yellow-400
                                [&::-webkit-slider-thumb]:rounded-full
                                [&::-webkit-slider-thumb]:cursor-pointer"
                        />
                        <button onClick={handleClickPlayPause}
                            className="w-8 h-8 flex items-center justify-center rounded-full text-white transition-all duration-200 hover:text-yellow-400">
                            {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                        </button>
                    </div>
                </div>

                {/* STATS */}
                <div className="grid grid-cols-2 gap-4 p-4 border-t border-slate-700 bg-[#0f172a] shrink-0">
                    <StatSection title="In Range" data={rowsInRange[0]} />
                    <StatSection title="Actual"   data={rowsActual[0]} />
                </div>

            </div>{/* end body */}
        </div>
    );
};

export default BaseBidAskComponent;